import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { NewsletterFilter } from '../newsletter.types';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { getCategoriesApi, getSkillsApi } from 'helpers/http/common';

interface FilterBuilderProps {
  filters: NewsletterFilter;
  onChange: (filters: NewsletterFilter) => void;
}

const FilterBuilder: React.FC<FilterBuilderProps> = ({ filters, onChange }) => {
  const [skillsOptions, setSkillsOptions] = useState<any[]>([]);
  const [categoriesOptions, setCategoriesOptions] = useState<any[]>([]);

  const updateFilter = (key: string, value: any) => {
    onChange({
      ...filters,
      [key]: value,
    });
  };

  const updateNestedFilter = (parent: string, key: string, value: any) => {
    onChange({
      ...filters,
      [parent]: {
        ...(filters[parent] || {}),
        [key]: value,
      },
    });
  };

  // Load skills options
  const loadSkillsOptions = async (inputValue: string) => {
    try {
      const response = await getSkillsApi(inputValue);
      if (response.status && response.data) {
        const options = response.data.map((skill: any) => ({
          label: skill.name,
          value: skill.id,
        }));
        // Merge with existing options to preserve selected values
        setSkillsOptions((prev) => {
          const merged = [...prev];
          options.forEach((opt) => {
            if (!merged.find((o) => o.value === opt.value)) {
              merged.push(opt);
            }
          });
          return merged;
        });
        return options;
      }
      return [];
    } catch (error) {
      console.error('Error loading skills:', error);
      return [];
    }
  };

  // Load categories options
  const loadCategoriesOptions = async (inputValue: string) => {
    try {
      const response = await getCategoriesApi(inputValue);
      if (response.status && response.data) {
        const options = response.data.map((category: any) => ({
          label: category.name,
          value: category.id,
        }));
        // Merge with existing options to preserve selected values
        setCategoriesOptions((prev) => {
          const merged = [...prev];
          options.forEach((opt) => {
            if (!merged.find((o) => o.value === opt.value)) {
              merged.push(opt);
            }
          });
          return merged;
        });
        return options;
      }
      return [];
    } catch (error) {
      console.error('Error loading categories:', error);
      return [];
    }
  };

  // Load selected skills/categories by ID
  useEffect(() => {
    const loadSelectedOptions = async () => {
      if (filters.userType === 'freelancers' || filters.userType === 'all') {
        // Load all skills and categories to ensure selected ones are available
        const [skillsResponse, categoriesResponse] = await Promise.all([
          getSkillsApi(''),
          getCategoriesApi(''),
        ]);

        if (skillsResponse.status && skillsResponse.data) {
          const skillsOpts = skillsResponse.data.map((skill: any) => ({
            label: skill.name,
            value: skill.id,
          }));
          setSkillsOptions(skillsOpts);
        }

        if (categoriesResponse.status && categoriesResponse.data) {
          const categoriesOpts = categoriesResponse.data.map(
            (category: any) => ({
              label: category.name,
              value: category.id,
            })
          );
          setCategoriesOptions(categoriesOpts);
        }
      } else {
        // Clear options if not freelancers/all
        setSkillsOptions([]);
        setCategoriesOptions([]);
      }
    };

    loadSelectedOptions();
  }, [filters.userType]);

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'BE', label: 'Belgium' },
    { value: 'CH', label: 'Switzerland' },
    { value: 'AT', label: 'Austria' },
    { value: 'SE', label: 'Sweden' },
    { value: 'NO', label: 'Norway' },
    { value: 'DK', label: 'Denmark' },
    { value: 'FI', label: 'Finland' },
    { value: 'PL', label: 'Poland' },
    { value: 'CZ', label: 'Czech Republic' },
    { value: 'IE', label: 'Ireland' },
    { value: 'PT', label: 'Portugal' },
    { value: 'GR', label: 'Greece' },
    { value: 'IN', label: 'India' },
    { value: 'PK', label: 'Pakistan' },
    { value: 'BD', label: 'Bangladesh' },
    { value: 'CN', label: 'China' },
    { value: 'JP', label: 'Japan' },
    { value: 'KR', label: 'South Korea' },
    { value: 'SG', label: 'Singapore' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'TH', label: 'Thailand' },
    { value: 'PH', label: 'Philippines' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'VN', label: 'Vietnam' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'IL', label: 'Israel' },
    { value: 'ZA', label: 'South Africa' },
    { value: 'EG', label: 'Egypt' },
    { value: 'NG', label: 'Nigeria' },
    { value: 'KE', label: 'Kenya' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'AR', label: 'Argentina' },
    { value: 'CL', label: 'Chile' },
    { value: 'CO', label: 'Colombia' },
    { value: 'PE', label: 'Peru' },
    { value: 'NZ', label: 'New Zealand' },
  ];

  const accountStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' },
  ];

  return (
    <div className="filter-builder">
      {/* User Type */}
      <div className="filter-section">
        <label className="filter-label">User Type</label>
        <Form.Select
          value={filters.userType}
          onChange={(e) => updateFilter('userType', e.target.value)}
          aria-label="Select user type filter"
        >
          <option value="all">All Users</option>
          <option value="clients">Clients Only</option>
          <option value="freelancers">Freelancers Only</option>
        </Form.Select>
      </div>

      {/* Last Login */}
      <div className="filter-section">
        <label className="filter-label">Last Login</label>
        <div className="duration-input">
          <Form.Select
            value={filters.lastLogin?.operator || 'more_than'}
            onChange={(e) =>
              updateNestedFilter('lastLogin', 'operator', e.target.value)
            }
          >
            <option value="more_than">More than</option>
            <option value="less_than">Less than</option>
          </Form.Select>
          <Form.Control
            type="number"
            placeholder="Value"
            min="0"
            value={filters.lastLogin?.value || ''}
            onChange={(e) =>
              updateNestedFilter('lastLogin', 'value', Number(e.target.value))
            }
          />
          <Form.Select
            value={filters.lastLogin?.unit || 'days'}
            onChange={(e) =>
              updateNestedFilter('lastLogin', 'unit', e.target.value)
            }
          >
            <option value="days">Days</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
          </Form.Select>
        </div>
      </div>

      {/* Registration Date */}
      <div className="filter-section">
        <label className="filter-label">Registration Date</label>
        <div className="mb-2">
          <Form.Control
            type="date"
            placeholder="From"
            value={filters.registrationDate?.from || ''}
            onChange={(e) =>
              updateNestedFilter('registrationDate', 'from', e.target.value)
            }
          />
        </div>
        <Form.Control
          type="date"
          placeholder="To"
          value={filters.registrationDate?.to || ''}
          onChange={(e) =>
            updateNestedFilter('registrationDate', 'to', e.target.value)
          }
        />
      </div>

      {/* Profile Status */}
      <div className="filter-section">
        <label className="filter-label">Email Verification</label>
        <Form.Select
          value={
            filters.emailVerified === null ||
            filters.emailVerified === undefined
              ? 'all'
              : filters.emailVerified
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'emailVerified',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
        >
          <option value="all">All</option>
          <option value="true">Verified</option>
          <option value="false">Not Verified</option>
        </Form.Select>
      </div>

      <div className="filter-section">
        <label className="filter-label">Profile Completion</label>
        <Form.Select
          value={
            filters.profileComplete === null ||
            filters.profileComplete === undefined
              ? 'all'
              : filters.profileComplete
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'profileComplete',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
        >
          <option value="all">All</option>
          <option value="true">Complete</option>
          <option value="false">Incomplete</option>
        </Form.Select>
      </div>

      {/* Job Activity */}
      <div className="filter-section">
        <label className="filter-label">Job Activity</label>

        {filters.userType === 'clients' && (
          <Form.Check
            type="checkbox"
            label="Never Posted a Job"
            checked={filters.jobActivity?.neverPostedJob || false}
            onChange={(e) =>
              updateNestedFilter(
                'jobActivity',
                'neverPostedJob',
                e.target.checked
              )
            }
            className="mb-2"
          />
        )}

        {filters.userType === 'freelancers' && (
          <Form.Check
            type="checkbox"
            label="Never sent a Proposal"
            checked={filters.jobActivity?.neverBidOnJob || false}
            onChange={(e) =>
              updateNestedFilter(
                'jobActivity',
                'neverBidOnJob',
                e.target.checked
              )
            }
            className="mb-2"
          />
        )}

        <Form.Select
          value={
            filters.jobActivity?.hasCompletedJobs === null ||
            filters.jobActivity?.hasCompletedJobs === undefined
              ? 'all'
              : filters.jobActivity?.hasCompletedJobs
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateNestedFilter(
              'jobActivity',
              'hasCompletedJobs',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
          className="mb-2"
        >
          <option value="all">Completed Jobs - All</option>
          <option value="true">Has Completed Jobs</option>
          <option value="false">No Completed Jobs</option>
        </Form.Select>

        <Form.Select
          value={
            filters.jobActivity?.hasActiveJobs === null ||
            filters.jobActivity?.hasActiveJobs === undefined
              ? 'all'
              : filters.jobActivity?.hasActiveJobs
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateNestedFilter(
              'jobActivity',
              'hasActiveJobs',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
          className="mb-2"
        >
          <option value="all">Active Jobs - All</option>
          <option value="true">Has Active Jobs</option>
          <option value="false">No Active Jobs</option>
        </Form.Select>

        <div className="number-range-input mt-2">
          <Form.Control
            type="number"
            placeholder="Min Jobs"
            min="0"
            value={filters.jobActivity?.minJobs || ''}
            onChange={(e) =>
              updateNestedFilter(
                'jobActivity',
                'minJobs',
                e.target.value ? parseInt(e.target.value) : undefined
              )
            }
          />
          <span className="text-muted">to</span>
          <Form.Control
            type="number"
            placeholder="Max Jobs"
            min="0"
            value={filters.jobActivity?.maxJobs || ''}
            onChange={(e) =>
              updateNestedFilter(
                'jobActivity',
                'maxJobs',
                e.target.value ? parseInt(e.target.value) : undefined
              )
            }
          />
        </div>
      </div>

      {/* Payment & Transaction */}
      <div className="filter-section">
        <label className="filter-label">Payment Status</label>
        <Form.Select
          value={
            filters.hasPaymentMethod === null ||
            filters.hasPaymentMethod === undefined
              ? 'all'
              : filters.hasPaymentMethod
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'hasPaymentMethod',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
          className="mb-2"
        >
          <option value="all">Payment Method - All</option>
          <option value="true">Has Payment Method</option>
          <option value="false">No Payment Method</option>
        </Form.Select>

        <Form.Select
          value={
            filters.hasTransaction === null ||
            filters.hasTransaction === undefined
              ? 'all'
              : filters.hasTransaction
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'hasTransaction',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
          className="mb-2"
        >
          <option value="all">Transactions - All</option>
          <option value="true">Has Transactions</option>
          <option value="false">No Transactions</option>
        </Form.Select>

        {(filters.hasTransaction === true ||
          filters.minTransactionAmount ||
          filters.maxTransactionAmount) && (
          <div className="number-range-input mt-2">
            <Form.Control
              type="number"
              placeholder="Min Amount"
              min="0"
              step="0.01"
              value={filters.minTransactionAmount || ''}
              onChange={(e) =>
                updateFilter(
                  'minTransactionAmount',
                  e.target.value ? parseFloat(e.target.value) : undefined
                )
              }
            />
            <span className="text-muted">to</span>
            <Form.Control
              type="number"
              placeholder="Max Amount"
              min="0"
              step="0.01"
              value={filters.maxTransactionAmount || ''}
              onChange={(e) =>
                updateFilter(
                  'maxTransactionAmount',
                  e.target.value ? parseFloat(e.target.value) : undefined
                )
              }
            />
          </div>
        )}
      </div>

      {/* Skills & Categories - Only for Freelancers */}
      {filters.userType === 'freelancers' || filters.userType === 'all' ? (
        <>
          <div className="filter-section">
            <label className="filter-label">Skills</label>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions={skillsOptions.length > 0 ? skillsOptions : true}
              loadOptions={loadSkillsOptions}
              value={skillsOptions.filter((opt) =>
                filters.skills?.includes(opt.value)
              )}
              onChange={(selected) =>
                updateFilter(
                  'skills',
                  selected ? selected.map((s) => s.value) : []
                )
              }
              placeholder="Search and select skills..."
              className="basic-multi-select"
              classNamePrefix="select"
              noOptionsMessage={({ inputValue }) =>
                inputValue ? 'No skills found' : 'Type to search skills...'
              }
            />
            <Form.Text className="text-muted">
              Search and select skills (for freelancers)
            </Form.Text>
          </div>

          <div className="filter-section">
            <label className="filter-label">Categories</label>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions={
                categoriesOptions.length > 0 ? categoriesOptions : true
              }
              loadOptions={loadCategoriesOptions}
              value={categoriesOptions.filter((opt) =>
                filters.categories?.includes(opt.value)
              )}
              onChange={(selected) =>
                updateFilter(
                  'categories',
                  selected ? selected.map((s) => s.value) : []
                )
              }
              placeholder="Search and select categories..."
              className="basic-multi-select"
              classNamePrefix="select"
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? 'No categories found'
                  : 'Type to search categories...'
              }
            />
            <Form.Text className="text-muted">
              Search and select categories (for freelancers)
            </Form.Text>
          </div>
        </>
      ) : null}

      {/* Location */}
      <div className="filter-section">
        <label className="filter-label">Countries</label>
        <Select
          isMulti
          options={countryOptions}
          value={countryOptions.filter((opt) =>
            filters.countries?.includes(opt.value)
          )}
          onChange={(selected) =>
            updateFilter(
              'countries',
              selected ? selected.map((s) => s.value) : []
            )
          }
          placeholder="Select countries..."
          className="basic-multi-select"
          classNamePrefix="select"
        />
      </div>

      {/* Engagement */}
      <div className="filter-section">
        <label className="filter-label">Engagement</label>
        <Form.Select
          value={
            filters.hasReviews === null || filters.hasReviews === undefined
              ? 'all'
              : filters.hasReviews
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'hasReviews',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
          className="mb-2"
        >
          <option value="all">Reviews - All</option>
          <option value="true">Has Reviews</option>
          <option value="false">No Reviews</option>
        </Form.Select>

        {filters.hasReviews === true && (
          <Form.Control
            type="number"
            placeholder="Minimum Rating (0-5)"
            min="0"
            max="5"
            step="0.1"
            value={filters.minRating || ''}
            onChange={(e) =>
              updateFilter(
                'minRating',
                e.target.value ? parseFloat(e.target.value) : undefined
              )
            }
            className="mb-2"
          />
        )}

        <Form.Select
          value={
            filters.hasDisputes === null || filters.hasDisputes === undefined
              ? 'all'
              : filters.hasDisputes
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'hasDisputes',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
        >
          <option value="all">Disputes - All</option>
          <option value="true">Has Disputes</option>
          <option value="false">No Disputes</option>
        </Form.Select>
      </div>

      {/* Account Status */}
      <div className="filter-section">
        <label className="filter-label">Account Status</label>
        <Select
          isMulti
          options={accountStatusOptions}
          value={accountStatusOptions.filter((opt) =>
            filters.accountStatus?.includes(opt.value as any)
          )}
          onChange={(selected) =>
            updateFilter(
              'accountStatus',
              selected ? selected.map((s) => s.value) : []
            )
          }
          placeholder="Select status..."
          className="basic-multi-select"
          classNamePrefix="select"
        />
      </div>

      {/* Identity Verification */}
      <div className="filter-section">
        <label className="filter-label">Identity Verification</label>
        <Form.Select
          value={
            filters.identityVerified === null ||
            filters.identityVerified === undefined
              ? 'all'
              : filters.identityVerified
              ? 'true'
              : 'false'
          }
          onChange={(e) =>
            updateFilter(
              'identityVerified',
              e.target.value === 'all' ? null : e.target.value === 'true'
            )
          }
        >
          <option value="all">All</option>
          <option value="true">Verified</option>
          <option value="false">Not Verified</option>
        </Form.Select>
      </div>
    </div>
  );
};

export default FilterBuilder;
